/**
 * One assessment → what the result screen draws. Pure, so every product rule
 * `bodycomp`'s contract states can be asserted without a browser (`D16`).
 *
 * ── THE RULES LIVE IN THE SHAPE, NOT IN THE COMPONENT ──────────────────────
 * Four of them cannot be enforced by a type, a lint rule or a gate, so each one
 * is made structurally impossible to break instead of written down as advice:
 *
 * 1. **A body-fat percentage is never shown without its range.** `percent`,
 *    `low` and `high` are not three fields on the view model — they are the
 *    three parameters of ONE message (`bodyfat_value`), so there is no way for
 *    a component to render the number and drop the interval. The 95% interval
 *    is 12–15 pp wide unless an anchor narrowed it, and a bare percentage reads
 *    as a measurement this is not.
 * 2. **A band is never asserted on a boundary.** {@link BodyWhtrView.band} is
 *    `null` exactly when `nearThreshold` is true, and `verdict` carries the
 *    "between X and Y" sentence in its place. The component has nothing to
 *    print, rather than a flag it may forget to read.
 * 3. **A warning is a code plus its parameters, and this file is what turns it
 *    into a key.** An unknown code becomes `warning.unknown`; a KNOWN code
 *    whose parameter did not arrive is DROPPED (see {@link warningOf}).
 * 4. **The notes are the model's description of the photographs, never a
 *    number and never markup.** They pass through untouched and the component
 *    puts them in text nodes.
 *
 * ── WHY IT RETURNS KEYS AND PARAMETERS, NOT SENTENCES ──────────────────────
 * Every string a screen shows for a value that arrived as DATA goes through
 * `@repo/client/i18n`'s translator and never `m[key]` (`T-164`): Paraglide
 * answers an unknown key with the raw key and a missing parameter with the word
 * `undefined`, and both are valid strings nothing reports. Returning
 * {@link BodyText} descriptors keeps that one hop in the component while the
 * tests can assert the exact key and the exact parameter object — which is the
 * only way to catch a missing parameter, since the rendered fallback
 * (`error.unrecognized`) looks like a generic server error.
 *
 * `translate` is taken all the same, for the fragments that are EMBEDDED in
 * another message: the two band names inside the boundary sentence and the
 * protocol fields inside `llm_not_comparable`. Those have to be rendered before
 * the outer message can be.
 */

import { type AppLocale, formatDay, joinList } from "@repo/client/format";
import { hasMessage } from "@repo/client/i18n";
import type {
  BodyAssessment,
  BodyAssessmentPhotoView,
  BodyAssessmentWarning,
  BodyWhtrBand,
} from "@repo/contract/training";

/** The values a message parameter may take — `MessageParams`, restated because
 *  `@repo/client/i18n` exports it only through `Translator`'s signature. */
export type BodyTextParams = Readonly<Record<string, string | number>>;

/** A message the component renders as `translate(key, params)`. */
export interface BodyText {
  readonly key: string;
  readonly params?: BodyTextParams;
}

/**
 * A warning, with the code it came from kept beside its sentence.
 *
 * The code is the React key and nothing else: two DIFFERENT codes this build
 * has no sentence for both render as `warning.unknown`, so the key alone is
 * not unique and a list keyed on it drops one of the two — the same shape of
 * bug the citation ledger is keyed positionally to avoid (`T-154`).
 */
export interface BodyWarningView extends BodyText {
  readonly code: string;
}

export interface BodyWhtrView {
  /**
   * `whtr_band.<band>` — the verdict, and `null` on a boundary.
   *
   * `bodycomp`'s contract: when `near_threshold` is true "the app must then say
   * the user is on the boundary instead of asserting the band"
   * (`app/schemas/response.py`:48-62). Nulling it is that rule: there is no
   * band label to draw, so no component can draw one.
   */
  readonly band: BodyText | null;
  /** The ratio itself, always two decimals: `0,48` / `0.48`. */
  readonly value: string;
  /** The band note, or the "between X and Y" sentence when on a boundary. */
  readonly verdict: BodyText;
}

export interface BodyFatView {
  /** `rfm`, `rfm+tape`, `rfm+anchor`, … — the label the service composed. */
  readonly method: BodyText;
  /** Which interval this is: the population one, or the anchored one. */
  readonly rangeNote: BodyText;
  /** Percentage AND range, inseparable. See the header, rule 1. */
  readonly value: BodyText;
}

export interface BodyDoneView {
  readonly bodyFat: BodyFatView | null;
  /**
   * `comparable === false`: the photo protocol failed and this number may not
   * be compared with any other (`bodycomp` README:146). It is still the
   * person's own measurement, so the row is SHOWN and badged, never hidden.
   */
  readonly notComparable: boolean;
  /** The LLM's description of the photographs. Plain text, at most five. */
  readonly notes: readonly string[];
  /** Set only when this row was replayed through `/v1/recompute` (`D23`). */
  readonly recomputed: BodyText | null;
  readonly warnings: readonly BodyWarningView[];
  readonly whtr: BodyWhtrView | null;
}

export type BodyResultView =
  | { readonly done: BodyDoneView; readonly kind: "done" }
  | {
      readonly kind: "failed";
      readonly reason: BodyText;
      /** The one photo to take again, the other kept (`D241`); `null` means
       *  both, which is also every refusal that named no photo. */
      readonly retakeView: BodyAssessmentPhotoView | null;
    }
  | { readonly kind: "pending" };

export interface BodyPresentOptions {
  readonly locale: AppLocale;
  readonly translate: (key: string, params?: BodyTextParams) => string;
}

const PREFIX = "body-assessment.";

/**
 * NICE's waist-to-height cut-offs and the two bands each one separates.
 *
 * These are `bodycomp`'s `band_thresholds` (`app/schemas/response.py`:57) and
 * they are NOT on the wire: `bodyAssessment.whtr` carries the band, the value,
 * its interval and `nearThreshold`, and nothing else. So they are restated
 * here, with their source, rather than derived from a number the contract does
 * not carry — and they are only ever used to NAME the two sides of a boundary
 * the server already decided we are on.
 */
const WHTR_THRESHOLDS: readonly {
  readonly lower: BodyWhtrBand;
  readonly upper: BodyWhtrBand;
  readonly value: number;
}[] = [
  { lower: "low", upper: "healthy", value: 0.4 },
  { lower: "healthy", upper: "increased", value: 0.5 },
  { lower: "increased", upper: "high", value: 0.6 },
];

/**
 * `Intl.NumberFormat` is not free to build, and this screen formats five
 * numbers per render.
 *
 * `@repo/client/format` has the same cache behind `formatKg`, which is
 * documented as "a load in kilograms" — a ratio and a percentage are neither,
 * and that package is not in this task's reach to gain an honest name. The
 * minimum digits are the reason a second one would be needed anyway: a ratio
 * is always two decimals (`0,50`, never `0,5`, which reads as a rounder number
 * than it is), and a percentage is at most one.
 */
const numberFormats = new Map<string, Intl.NumberFormat>();

function formatNumber(
  value: number,
  locale: AppLocale,
  minimumFractionDigits: number,
  maximumFractionDigits: number
): string {
  const cacheKey = `${locale}|${minimumFractionDigits}|${maximumFractionDigits}`;
  let format = numberFormats.get(cacheKey);
  if (!format) {
    format = new Intl.NumberFormat(locale, {
      maximumFractionDigits,
      minimumFractionDigits,
    });
    numberFormats.set(cacheKey, format);
  }
  return format.format(value);
}

/**
 * The cut-off this value sits on, by absolute distance.
 *
 * Called only when the server said `nearThreshold`, so "nearest" is exact
 * rather than a guess: within 0.02 of an edge, no two cut-offs 0.1 apart can
 * both be candidates.
 */
function nearestThreshold(value: number): (typeof WHTR_THRESHOLDS)[number] {
  let nearest = WHTR_THRESHOLDS[0] as (typeof WHTR_THRESHOLDS)[number];
  for (const threshold of WHTR_THRESHOLDS) {
    if (Math.abs(value - threshold.value) < Math.abs(value - nearest.value)) {
      nearest = threshold;
    }
  }
  return nearest;
}

function bandText(band: BodyWhtrBand): BodyText {
  return { key: `${PREFIX}whtr_band.${band}` };
}

function whtrOf(
  whtr: NonNullable<BodyAssessment["whtr"]>,
  options: BodyPresentOptions
): BodyWhtrView {
  const value = formatNumber(whtr.value, options.locale, 2, 2);
  if (!whtr.nearThreshold) {
    return {
      band: bandText(whtr.band),
      value,
      verdict: { key: `${PREFIX}whtr_band_note.${whtr.band}` },
    };
  }
  const edge = nearestThreshold(whtr.value);
  return {
    band: null,
    value,
    verdict: {
      key: `${PREFIX}whtr_near_threshold`,
      params: {
        lower: options.translate(bandText(edge.lower).key),
        upper: options.translate(bandText(edge.upper).key),
      },
    },
  };
}

function bodyFatOf(
  bodyFat: NonNullable<BodyAssessment["bodyFat"]>,
  locale: AppLocale
): BodyFatView {
  const percent = (value: number) => formatNumber(value, locale, 0, 1);
  return {
    method: {
      key: `${PREFIX}bodyfat_method`,
      params: { method: bodyFat.method },
    },
    rangeNote: { key: `${PREFIX}bodyfat_range_${bodyFat.rangeModel}` },
    value: {
      key: `${PREFIX}bodyfat_value`,
      params: {
        high: percent(bodyFat.high),
        low: percent(bodyFat.low),
        percent: percent(bodyFat.percent),
      },
    },
  };
}

/**
 * One warning, as a key plus its parameters — or `null`, which means DROP it.
 *
 * ── WHY A KNOWN CODE CAN STILL BE DROPPED ──────────────────────────────────
 * Two of the eleven codes carry a parameter: `diagnostic_spread` needs
 * `spreadPp` and `llm_not_comparable` needs `fields`, and both are `nullable`
 * on the wire because every OTHER code leaves them null. A code that arrives
 * without the value its sentence interpolates has nothing renderable behind
 * it: the translator answers `error.unrecognized` for a missing parameter
 * (`T-164`), which inside a list of observations about someone's body reads as
 * a crash. `warning.unknown` is worse still — it says "update the app", which
 * is not the remedy for a server that sent half a warning.
 *
 * So it is dropped, which is the same answer the summary screen reached for a
 * `nextSessionChanges` reason this build cannot say (`@repo/client/i18n`'s
 * `hasMessage` note). Nothing else on the screen depends on it:
 * `llm_not_comparable` always rides with `comparable: false`, which draws its
 * own badge and explanation.
 *
 * ── AND WHY THE CATALOGUE, NOT `BODYCOMP_WARNING_CODES`, DECIDES `unknown` ──
 * The contract lists the eleven codes the service publishes today, and its own
 * docblock says a renderer switches on them. What a renderer actually needs to
 * know is narrower: whether THIS build has a sentence. `hasMessage` answers
 * exactly that, so a twelfth code from a service on its own deploy clock and a
 * code that the contract lists but T102 never wrote a sentence for both land on
 * `warning.unknown` instead of one of them rendering as `error.unrecognized`.
 */
function warningOf(
  warning: BodyAssessmentWarning,
  options: BodyPresentOptions
): BodyWarningView | null {
  const { code } = warning;
  const key = `${PREFIX}warning.${code}`;
  if (!hasMessage(key)) {
    return { code, key: `${PREFIX}warning.unknown` };
  }
  if (code === "diagnostic_spread") {
    if (warning.spreadPp === null) {
      return null;
    }
    return {
      code,
      key,
      params: { spread: formatNumber(warning.spreadPp, options.locale, 0, 2) },
    };
  }
  if (code === "llm_not_comparable") {
    const labels = (warning.fields ?? [])
      .map((field) => `${PREFIX}protocol_field.${field}`)
      .filter((fieldKey) => hasMessage(fieldKey))
      .map((fieldKey) => options.translate(fieldKey));
    if (labels.length === 0) {
      return null;
    }
    return { code, key, params: { fields: joinList(labels, options.locale) } };
  }
  return { code, key };
}

/**
 * The failure sentence.
 *
 * `errorCode` is `bodycomp`'s own (`POSE_INVALID`, `SEGMENTATION_FAILED`,
 * `EXPIRED`, …) and arrives with no text at all — a failed async job answers
 * HTTP 200 with a code. Each of T102's `error.<CODE>` messages already carries
 * the remedy in its own sentence, which is why there is no second "hint" beside
 * it: there is one thing to say and the catalogue says it.
 *
 * The catalogue is again what decides "known", for the reason in
 * {@link warningOf}, and `null` — a `failed` row with no code at all — takes
 * the same generic sentence as a code nobody has written yet.
 */
function failureOf(errorCode: string | null, reason: string | null): BodyText {
  // A `POSE_INVALID` whose `reason` arrived says WHICH check failed (`D241`),
  // and that check's own sentence is the sharper remedy. A reason this build
  // has no sentence for falls back to the code's.
  if (errorCode === "POSE_INVALID" && reason !== null) {
    const reasonKey = `${PREFIX}pose_reason.${reason}`;
    if (hasMessage(reasonKey)) {
      return { key: reasonKey };
    }
  }
  const key = `${PREFIX}error.${errorCode ?? ""}`;
  return { key: hasMessage(key) ? key : `${PREFIX}error.unknown` };
}

export function presentBodyResult(
  assessment: BodyAssessment,
  options: BodyPresentOptions
): BodyResultView {
  if (assessment.status === "pending") {
    return { kind: "pending" };
  }
  if (assessment.status === "failed") {
    const detail = assessment.errorDetail;
    return {
      kind: "failed",
      reason: failureOf(assessment.errorCode, detail?.reason ?? null),
      retakeView: detail?.view ?? null,
    };
  }
  const warnings: BodyWarningView[] = [];
  for (const warning of assessment.warnings) {
    const text = warningOf(warning, options);
    if (text !== null) {
      warnings.push(text);
    }
  }
  return {
    done: {
      bodyFat:
        assessment.bodyFat === null
          ? null
          : bodyFatOf(assessment.bodyFat, options.locale),
      notComparable: assessment.comparable === false,
      notes: assessment.notes,
      recomputed:
        assessment.recomputedAt === null
          ? null
          : {
              key: `${PREFIX}recomputed`,
              params: {
                date: formatDay(
                  new Date(assessment.recomputedAt),
                  options.locale
                ),
              },
            },
      warnings,
      whtr: assessment.whtr === null ? null : whtrOf(assessment.whtr, options),
    },
    kind: "done",
  };
}
